import { PopperProps } from '@mui/material'
import cn from 'classnames'
import DropdownMenu from 'ext-ui/components/Dropdown/components/DropdownMenu/DropdownMenu'
import { DropdownOptionModel } from 'ext-ui/components/Dropdown/components/DropdownOption/DropdownOption'
import {
  DropdownOptionGroup,
  DropdownValue,
  useDropdownLogic,
} from 'ext-ui/components/Dropdown/hooks/useDropdownLogic'
import { TextInputProps } from 'ext-ui/components/TextInput/TextInput'
import React, {
  HTMLInputAutoCompleteAttribute,
  useEffect,
  useImperativeHandle,
  useRef,
} from 'react'
import CreditCardDropdownHandle from 'src/components/CreditCardDropdownV2/CreditCardDropdownHandle'
import CreditCardDropdownOption, {
  CreditCardDropdownOptionDetails,
} from 'src/components/CreditCardDropdownV2/CreditCardDropdownOption'
import { useCreditCardDropdownOptions } from 'src/components/CreditCardDropdownV2/useCreditCardDropdownOptions'
import { useTranslate } from 'src/localization/Trns'
import { assertNever } from 'src/queries/agent/network/agent.network'

import styles, { emptyState, loadingOption } from './CreditCardDropdownV2.style'

export type { DropdownOptionGroup, DropdownValue }

const DropdownEmptyState: React.FC<{
  icon?: React.ReactNode
  title: string
  subtitle?: string
}> = ({ icon, title, subtitle }) => (
  <div css={emptyState}>
    {icon}
    <div className={'title'}>{title}</div>
    {subtitle && <div className={'subtitle'}>{subtitle}</div>}
  </div>
)

export type CreditCardDropdownV2Props = Omit<TextInputProps, 'iconRight'> & {
  selectedCreditCardIds: Set<string> | undefined
  /**
   * Selection handler - supports both sync and async operations
   * Return Promise<void> for async operations with optimistic updates
   */
  onSelect: (
    value: DropdownValue<CreditCardDropdownOptionDetails>,
  ) => void | Promise<void>
  onCancel?: () => void
  onReset?: () => void
  openOnMount?: boolean
  /**
   * how many options are visible in the DropdownMenu before scrolling is necessary
   * @default 5
   */
  dropdownOptionsCount?: number
  /**
   * whether to show the search input inside the dropdown menu
   * @default false
   */
  searchable?: boolean
  emptyMessage?: {
    icon?: React.ReactNode
    title: string
    subtitle?: string
  }
  noResultsMessage?: {
    icon?: React.ReactNode
    title: string
    subtitle?: string
  }
  dropdownContainer?: PopperProps['container']
  /**
   * specifies whether the browser should enable autocomplete for the input field
   * NOTE: if using infiniteScrollProps, autocomplete will not work for items beyond the initial fetch limit
   */
  autoComplete?: HTMLInputAutoCompleteAttribute
  location?: string
}

const CreditCardDropdownV2 = React.forwardRef<
  HTMLButtonElement | null,
  CreditCardDropdownV2Props
>((props, externalRef) => {
  const {
    selectedCreditCardIds,
    onSelect,
    onCancel,
    onReset,
    className,
    openOnMount,
    dropdownOptionsCount,
    searchable,
    emptyMessage = {
      title: 'No options available',
      subtitle: 'There are no options to select from at this time.',
    },
    noResultsMessage = {
      title: 'No matching results',
      subtitle: 'Please check for any typos, or search for a different option.',
    },
    dropdownContainer,
    variant = 'medium',
    label,
    placeholder,
    loadingState,
    microcopy,
  } = props

  const translate = useTranslate()

  type CardOpt = DropdownOptionModel<CreditCardDropdownOptionDetails>

  const optionCatalogRef = useRef<Map<string, CardOpt>>(new Map())

  const { optionGroups, infiniteScrollProps } = useCreditCardDropdownOptions({
    location: props.location,
  })

  useEffect(() => {
    const catalog = optionCatalogRef.current
    for (const group of optionGroups) {
      for (const opt of group.options) {
        const id = String(opt.value)
        const prev = catalog.get(id)
        if (prev !== opt) catalog.set(id, opt)
      }
    }
  }, [optionGroups])

  const selectedIds = React.useMemo(
    () => new Set(Array.from(selectedCreditCardIds ?? []).map(String)),
    [selectedCreditCardIds],
  )

  const selectedOptions: CardOpt[] = React.useMemo(() => {
    const out: CardOpt[] = []
    const catalog = optionCatalogRef.current
    for (const id of selectedIds) {
      const opt = catalog.get(id)
      if (opt) out.push(opt)
    }
    return out
  }, [selectedIds, optionGroups])

  const {
    rows,
    open,
    getMenuProps,
    getItemProps,
    getDropdownHandleProps,
    getSearchInputProps,
    containerRef,
    optimisticSelectedValues,
    asyncState,
    clearSearch,
  } = useDropdownLogic<CreditCardDropdownOptionDetails>({
    groups: optionGroups,
    selectedValues: selectedOptions,
    infiniteScrollProps,
    onSelect,
    onCancel,
    openOnMount,
    searchable,
    loadingState,
    noneLabel: '',
    multi: true,
  })

  const handleProps = getDropdownHandleProps<HTMLButtonElement>()
  useImperativeHandle<HTMLButtonElement | null, HTMLButtonElement | null>(
    externalRef,
    () => handleProps.ref.current,
  )

  // reset on close
  const searchInputProps = getSearchInputProps()
  const prevOpenRef = useRef(false)

  useEffect(() => {
    if (prevOpenRef.current && !open) {
      clearSearch()
      infiniteScrollProps?.fetch?.(undefined, 0)
    }
    prevOpenRef.current = open
  }, [open, searchInputProps, infiniteScrollProps])

  return (
    <div
      ref={containerRef}
      className={cn('CreditCardDropdownV2', className, {
        selected: (selectedCreditCardIds?.size ?? 0) > 0,
      })}
      css={styles}
    >
      <CreditCardDropdownHandle
        open={open}
        label={label}
        variant={variant}
        placeholder={placeholder}
        loadingState={loadingState ?? asyncState}
        selectedValues={selectedOptions}
        microcopy={microcopy}
        searchable={searchable}
        {...handleProps}
      />
      <DropdownMenu
        {...getMenuProps()}
        variant={variant}
        container={dropdownContainer}
        open={open}
        dropdownOptionsCount={dropdownOptionsCount}
        searchProps={getSearchInputProps()}
        searchable={true}
        infiniteScrollProps={infiniteScrollProps}
        onReset={rows.length > 1 ? onReset : undefined}
      >
        {rows.map((row, index) => {
          switch (row.kind) {
            case 'heading':
              return (
                <div className={cn(variant)} key={`heading-${index}`}>
                  {row.heading}
                </div>
              )
            case 'loading':
              return (
                <div
                  className={cn('loading', variant)}
                  css={loadingOption}
                  key={'loading'}
                >
                  {translate('Loading...')}
                </div>
              )
            case 'empty':
              return (
                <DropdownEmptyState
                  key={'empty'}
                  {...(row.hasSearchQuery ? noResultsMessage : emptyMessage)}
                />
              )
            case 'option': {
              const opt = row.option
              // never show (none) option as selected
              const selected =
                opt.value !== '' &&
                optimisticSelectedValues
                  ?.map((v) => v.value)
                  .includes(opt.value)
              return (
                <CreditCardDropdownOption
                  key={`${opt.value}-${index}`}
                  {...opt}
                  selected={selected}
                  variant={variant}
                  multi={rows.length > 1}
                  {...getItemProps({ item: row, index })}
                />
              )
            }
            default:
              assertNever(row)
          }
        })}
      </DropdownMenu>
    </div>
  )
})

export default CreditCardDropdownV2
